import { ErrorInfoIcon } from '@repo/icons';
import { cn } from '@repo/utils/cn';
import * as React from 'react';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  hasError?: boolean;
  hideErrorIcon?: boolean;
  icon?: React.ReactElement;
  label?: string;
}

/**
 * A simple input field
 *
 * We use the ! symbol prefix for the TailwindCSS classes to override any styles applied by browser plugins i.e. password managers
 */
const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, disabled, hasError, hideErrorIcon = false, icon, label, ...props }, ref) => {
    return (
      <div className="relative flex w-full flex-col items-center">
        {label && <label className="py-1">{label}</label>}
        {icon &&
          React.cloneElement(icon, {
            className: cn(
              'absolute top-1/2 -translate-y-1/2 size-4 shrink-0 vt-input-default-text-normal',
              'ltr:left-2 rtl:right-2',
              (icon.props as any).className
            ),
          } as any)}
        <input
          type={type}
          disabled={disabled}
          aria-disabled={disabled}
          className={cn(
            'vt-input-default-bg-normal vt-input-default-stroke-normal focus:vt-input-default-bg-focus vt-input-default-text-normal placeholder:vt-input-default-text-normal focus:vt-input-default-text-focus focus:vt-input-default-stroke-focus vt-input-text-size-normal h-10 w-full rounded-[4px] border p-1.5 leading-3 focus:shadow-[0px_0px_10px_0px_var(--input-focus-shadow-normal)] focus:ring-transparent',
            icon && 'ltr:pl-8 rtl:pr-8',
            disabled &&
              'vt-input-default-bg-disabled vt-input-stroke-disabled vt-input-default-text-disabled placeholder:vt-input-default-text-disabled focus:vt-input-focus-shadow-disabled vt-input-text-size-disabled cursor-not-allowed',
            hasError &&
              'vt-input-error-bg-normal vt-input-error-stroke-normal vt-input-error-text-normal focus:vt-input-error-stroke-focus focus:vt-input-error-bg-focus vt-input-icon-error-fill-normal shadow-[0px_0px_10px_0px_var(--input-error-shadow-normal)] ltr:pr-5 rtl:pl-5',
            className
          )}
          ref={ref}
          {...props}
        />
        {!hideErrorIcon && hasError && (
          <ErrorInfoIcon className="absolute top-1/2 -translate-y-1/2 ltr:right-2 rtl:left-2" />
        )}
      </div>
    );
  }
);
Input.displayName = 'Input';

export { Input };
